import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import VaultoLogo from './VaultoLogo';
import {
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const Footer = ({ variant = 'default' }) => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleFeaturesClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on home page, just scroll to features
      const featuresSection = document.getElementById('features');
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        const featuresSection = document.getElementById('features');
        if (featuresSection) {
          featuresSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };
  
  // Different styles based on variant
  const getFooterStyles = () => {
    if (variant === 'dark') {
      return 'bg-gray-900 border-t border-gray-800';
    }
    return 'bg-white border-t border-gray-200';
  };

  const getTextStyles = () => {
    if (variant === 'dark') {
      return {
        primary: 'text-white',
        secondary: 'text-gray-300',
        muted: 'text-gray-400',
        link: 'text-gray-300 hover:text-white'
      };
    }
    return {
      primary: 'text-gray-900',
      secondary: 'text-gray-600',
      muted: 'text-gray-500',
      link: 'text-gray-600 hover:text-gray-900'
    };
  };

  const textStyles = getTextStyles();

  // Use useMemo to recreate navigation when language changes
  const navigation = useMemo(() => ({
    product: [
      { name: t('footer.product.features'), href: '/#features', isAnchor: true },
      { name: t('footer.product.pricing'), href: '/pricing' },
      { name: t('footer.product.dashboard'), href: '/dashboard' },
      { name: t('footer.product.backups'), href: '/backups' },
    ],
    support: [
      { name: t('footer.support.helpCenter'), href: '/help' },
      { name: t('footer.support.contact'), href: '/contact' },
      { name: t('footer.support.status'), href: '/status' },
    ],
    legal: [
      { name: t('footer.legal.privacy'), href: '/privacy' },
      { name: t('footer.legal.terms'), href: '/terms' },
      { name: t('footer.legal.cookies'), href: '/cookies' },
      { name: t('footer.legal.security'), href: '/security' },
    ],
    company: [
      { name: t('footer.company.about'), href: '/about' },
      { name: t('footer.company.blog'), href: '/blog' },
      { name: t('footer.company.careers'), href: '/careers' },
      { name: t('footer.company.press'), href: '/press' },
      { name: 'Affiliates', href: '/affiliates' },
    ],
  }), [t, i18n.language]);

  const socialLinks = [
    { name: 'X', href: 'https://x.com/vaultosocial', icon: '𝕏' },
    { name: 'Facebook', href: 'https://www.facebook.com/VaultoSocial/', icon: 'f' },
    { name: 'Instagram', href: 'https://instagram.com/vaultosocial', icon: '📷' },
  ];

  return (
    <footer className={getFooterStyles()}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <VaultoLogo 
                className="h-14 w-auto" 
                showText={true}
              />
            </Link>
            <p className={`${textStyles.secondary} text-sm mb-4 max-w-sm`}>
              {t('footer.description')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2">
              <div className={`flex items-center ${textStyles.muted} text-sm`}>
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                <a href="mailto:admin@vaulto.social" className={textStyles.link}>
                  admin@vaulto.social
                </a>
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className={`${textStyles.primary} text-sm font-semibold uppercase tracking-wider mb-4`}>
              {t('footer.product.title')}
            </h3>
            <ul className="space-y-3">
              {navigation.product.map((item) => (
                <li key={item.name}>
                  {item.isAnchor ? (
                    <a
                      href={item.href}
                      onClick={handleFeaturesClick}
                      className={`${textStyles.link} text-sm transition-colors duration-200 cursor-pointer`}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`${textStyles.link} text-sm transition-colors duration-200`}
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className={`${textStyles.primary} text-sm font-semibold uppercase tracking-wider mb-4`}>
              {t('footer.support.title')}
            </h3>
            <ul className="space-y-3">
              {navigation.support.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`${textStyles.link} text-sm transition-colors duration-200`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className={`${textStyles.primary} text-sm font-semibold uppercase tracking-wider mb-4`}>
              {t('footer.legal.title')}
            </h3>
            <ul className="space-y-3">
              {navigation.legal.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`${textStyles.link} text-sm transition-colors duration-200`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className={`${textStyles.primary} text-sm font-semibold uppercase tracking-wider mb-4`}>
              {t('footer.company.title')}
            </h3>
            <ul className="space-y-3">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`${textStyles.link} text-sm transition-colors duration-200`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-12 pt-8 border-t ${variant === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className={`${textStyles.muted} text-sm`}>
              &copy; {currentYear} Vaulto. {t('footer.copyright')}
            </div>
            
            {/* Quick Legal Links */}
            <div className="mt-4 md:mt-0 flex items-center space-x-6">
              <Link
                to="/privacy"
                className={`${textStyles.link} text-sm transition-colors duration-200`}
              >
                {t('footer.legal.privacy')}
              </Link>
              <Link
                to="/terms"
                className={`${textStyles.link} text-sm transition-colors duration-200`}
              >
                {t('footer.legal.terms')}
              </Link>
              <Link
                to="/cookies"
                className={`${textStyles.link} text-sm transition-colors duration-200`}
              >
                {t('footer.legal.cookies')}
              </Link>
            </div>

            {/* Social Links */}
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${textStyles.link} text-lg hover:scale-110 transition-all duration-200`}
                  title={item.name}
                >
                  {item.name === 'Facebook' ? (
                    <span className={`font-bold ${variant === 'dark' ? 'bg-gray-300 text-gray-900' : 'bg-gray-600 text-white'} rounded-full w-7 h-7 flex items-center justify-center text-base hover:bg-opacity-80`}>
                      {item.icon}
                    </span>
                  ) : (
                    item.icon
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 